import type { SupabaseClient } from "@supabase/supabase-js";
import { Upload } from "tus-js-client";
import { asError } from "../utils/error";
import { projectURL } from "./supabaseConstants";
import type {
  SignedUploadable,
  SignedUrlResponse,
  SupabaseContextType,
  SupabasePhoneStream,
  SupabaseStream,
  UploadRecordingToSupabaseArgs,
} from "./supabaseTypes";
import type { Database } from "../types/gen/database.types";
import { isPhoneComplete, isPhoneStatus, type PhoneStatus } from "../utils/phoneUtils";

export function uploadRecordingToSupabase({
  file,
  signedUrl,
  contentType,
  setUploadPercentage,
}: UploadRecordingToSupabaseArgs): Promise<void> {
  const { token, bucket, file_path } = signedUrl;
  if (!token) {
    const message = "Error uploading recording: token is undefined";
    console.error(message);
    return Promise.reject(new Error(message));
  }
  return new Promise<void>((resolve, reject) => {
    const upload = new Upload(file, {
      endpoint: projectURL + `/storage/v1/upload/resumable/sign`,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      headers: {
        "x-client-info": "web",
        "x-signature": token,
        "x-upsert": "true",
      },
      uploadDataDuringCreation: true,
      removeFingerprintOnSuccess: true,
      metadata: {
        bucketName: bucket,
        objectName: file_path,
        contentType: contentType,
        cacheControl: "3600",
      },
      chunkSize: 6 * 1024 * 1024, // NOTE: it must be set to 6MB (for now) do not change it
      onError: function (error) {
        reject(error);
      },
      onProgress: function (bytesUploaded: number, bytesTotal: number) {
        if (setUploadPercentage) {
          setUploadPercentage(Math.round((bytesUploaded / bytesTotal) * 100));
        }
      },
      onSuccess: function () {
        resolve();
      },
    });

    // Check if there are any previous uploads to continue.
    upload
      .findPreviousUploads()
      .then(function (previousUploads) {
        // Found previous uploads so we select the first one.
        const firstUpload = previousUploads[0];
        if (firstUpload) {
          upload.resumeFromPreviousUpload(firstUpload);
        }

        // Start the upload
        upload.start();
      })
      .catch((error: unknown) => {
        const finalError: Error = asError(
          error,
          new Error("Encountered unknown error while resuming uploads")
        );
        reject(finalError);
      });
  });
}

export function unsubscribeStream(
  streamName: string,
  supabase: SupabaseClient
) {
  const channels = supabase.getChannels();
  return Promise.all(
    channels.map((value) => {
      if (value.topic === streamName) {
        return supabase.removeChannel(value);
      }
      return Promise.resolve();
    })
  );
}

export interface SubscribeToStreamArgs<T> {
  filter: string;
  streamName: string;
  onChange: (data: T) => void;
  testCompleted: (data: T) => boolean;
  completed: () => void;
  supabase: SupabaseClient;
}

export async function subscribeToStream<T extends { [key: string]: unknown }>({
  filter,
  streamName,
  onChange,
  testCompleted,
  completed,
  supabase,
}: SubscribeToStreamArgs<T>) {
  await unsubscribeStream(streamName, supabase);

  supabase
    .channel(streamName)
    .on<T>(
      "postgres_changes",
      { event: "UPDATE", schema: "public", table: streamName, filter },
      (payload) => {
        const data = payload["new"];
        onChange(data);
        if (testCompleted(data)) {
          void unsubscribeStream(streamName, supabase);
          completed();
        }
      }
    )
    .subscribe();
}

export function uploadToSupabaseSignedURL(supabase: SupabaseClient<Database>) {
  return async (file: SignedUploadable, signedUrl: SignedUrlResponse) => {
    try {
      const { token, bucket, file_path } = signedUrl;
      const result = await supabase.storage
        .from(bucket)
        .uploadToSignedUrl(file_path, token, file, {
          upsert: true,
        });
      if (result.error) {
        const { message } = asError(result.error);
        console.error("Error uploading file:", message);
        throw new Error(message); // Properly throw an error on failure
      }
    } catch (error: unknown) {
      console.error("Error uploading file:", error);
      const { message } = asError(error);
      throw new Error(`Error uploading file: ${message}`);
    }
  };
}

export function getCurrentStateOfNotes(
  supabaseClient: SupabaseClient<Database>
) {
  return async (session: string, node_id?: string): Promise<string> => {
    const queryBuilder = supabaseClient.from("notes_changes").select("text");
    const query = node_id
      ? queryBuilder.eq("session_id_and_node_id", `${session}_${node_id}`)
      : queryBuilder.eq("session_id", session);

    const { data } = await query;
    return data?.[0]?.text || "Writing notes...";
  };
}

export function getCurrentStateOfDischarge(
  supabaseClient: SupabaseClient<Database>
) {
  return async (session: string): Promise<string> => {
    const { data } = await supabaseClient
      .from("discharge_changes")
      .select()
      .eq("session_id", session);
    return data?.[0]?.["text"] || "Writing notes...";
  };
}

export function getCurrentStateOfPreHistory(
  supabaseClient: SupabaseClient<Database>
) {
  return async (session: string): Promise<string> => {
    const { data } = await supabaseClient
      .from("prehistory_changes")
      .select()
      .eq("session_id", session);
    return data?.[0]?.["text"] || "{}";
  };
}

export function getCurrentStateOfPhone(
  supabaseClient: SupabaseClient<Database>
) {
  return async (session: string, recordingId: string): Promise<PhoneStatus> => {
    const { data } = await supabaseClient
      .from("phone_changes")
      .select()
      .eq("session_id_and_recording_id", `${session}_${recordingId}`);
    if (!data || !data[0]?.["status"]) {
      return "unknown";
    }
    const status = data[0]["status"];
    if (isPhoneStatus(status)) {
      return status;
    }
    console.warn(`Invalid phone status received: ${status}. Defaulting to "unknown".`)
    return "unknown";
  };
}

export function subscribeToNotesStream(
  supabaseClient: SupabaseClient<Database>
) {
  return (
    session: string,
    node_id: string | undefined,
    method: (notes: string | undefined, diagram: string | undefined) => void,
    completed: () => void
  ) =>
    subscribeToStream<SupabaseStream>({
      streamName: "notes_changes",
      filter: node_id
        ? `session_id_and_node_id=eq.${session}_${node_id}`
        : `session_id=eq.${session}`,
      onChange: (stream) => {
        const notes = stream.text || undefined;
        const diagram = stream.diagram || undefined;
        method(notes, diagram);
      },
      testCompleted: (data) => data["completed"],
      completed,
      supabase: supabaseClient,
    });
}

export function subscribeToPreHistoryStream(
  supabaseClient: SupabaseClient<Database>
) {
  return (
    session: string,
    method: (value: string) => void,
    completed: () => void
  ) =>
    subscribeToStream<SupabaseStream>({
      streamName: "prehistory_changes",
      filter: `session_id=eq.${session}`,
      onChange: (data) => {
        if (data["text"]) {
          method(data["text"]);
        }
      },
      testCompleted: (data) => data["completed"],
      completed,
      supabase: supabaseClient,
    });
}

export function subscribeToDischargeStream(
  supabaseClient: SupabaseClient<Database>
) {
  return (
    session: string,
    method: (value: string) => void,
    completed: () => void
  ) =>
    subscribeToStream<SupabaseStream>({
      streamName: "discharge_changes",
      filter: `session_id=eq.${session}`,
      onChange: (data) => {
        if (data["text"]) {
          method(data["text"]);
        }
      },
      testCompleted: (data) => data["completed"],
      completed,
      supabase: supabaseClient,
    });
}

export function subscribeToPhoneStream(
  supabaseClient: SupabaseClient<Database>
) {
  return (
    sessionId: string,
    recordingId: string,
    onStatusChange: (status: PhoneStatus) => void,
    completed: () => void
  ) =>
    subscribeToStream<SupabasePhoneStream>({
      streamName: "phone_changes",
      filter: `session_id_and_recording_id=eq.${sessionId}_${recordingId}`,
      onChange: (data) => {
        if (isPhoneStatus(data["status"])) {
          onStatusChange(data["status"]);
        } else {
          console.warn(
            `Invalid phone status received: ${data["status"] as string}. Defaulting to "unknown".`
          );
          onStatusChange("unknown");
        }
      },
      testCompleted: (data) => {
        const status = data["status"];
        return isPhoneComplete(status);
      },
      completed,
      supabase: supabaseClient,
    });
}

export function removeNotesSubscriptions(
  supabaseClient: SupabaseClient<Database>
) {
  return async () => {
    await unsubscribeStream("notes_changes", supabaseClient);
  };
}

export function removePreHistorySubscriptions(
  supabaseClient: SupabaseClient<Database>
) {
  return async () => {
    await unsubscribeStream("prehistory_changes", supabaseClient);
  };
}

export function removeDischargeSubscriptions(
  supabaseClient: SupabaseClient<Database>
) {
  return async () => {
    await unsubscribeStream("discharge_changes", supabaseClient);
  };
}

export function removePhoneSubscriptions(
  supabaseClient: SupabaseClient<Database>
) {
  return async () => {
    await unsubscribeStream("phone_changes", supabaseClient);
  };
}

export function removeSubscriptions(supabaseClient: SupabaseClient<Database>) {
  return async () => {
    await supabaseClient.removeAllChannels();
  };
}

export const defaultSupabaseContext: SupabaseContextType = {
  uploadToSupabaseSignedURL: () => {
    throw new Error("uploadToSupabaseSignedURL not implemented");
  },
  getCurrentStateOfNotes: () => {
    throw new Error("getCurrentStateOfNotes not implemented");
  },
  getCurrentStateOfDischarge: () => {
    throw new Error("getCurrentStateOfDischarge not implemented");
  },
  getCurrentStateOfPreHistory: () => {
    throw new Error("getCurrentStateOfPreHistory not implemented");
  },
  getCurrentStateOfPhone: () => {
    throw new Error("getCurrentStateOfPhone not implemented");
  },
  subscribeToNotesStream: () => {
    throw new Error("subscribeToNotesStream not implemented");
  },
  removeNotesSubscriptions: () => {
    throw new Error("removeNotesSubscriptions not implemented");
  },
  subscribeToPreHistoryStream: () => {
    throw new Error("subscribeToPreHistoryStream not implemented");
  },
  removePreHistorySubscriptions: () => {
    throw new Error("removePreHistorySubscriptions not implemented");
  },
  subscribeToDischargeStream: () => {
    throw new Error("subscribeToDischargeStream not implemented");
  },
  subscribeToPhoneStream: () => {
    throw new Error("subscribeToPhoneStream not implemented");
  },
  removeDischargeSubscriptions: () => {
    throw new Error("removeDischargeSubscriptions not implemented");
  },
  removePhoneSubscriptions: () => {
    throw new Error("removePhoneSubscriptions not implemented");
  },
  removeSubscriptions: () => {
    throw new Error("removeSubscriptions not implemented");
  },
};
